import { useState, useEffect } from 'react';
import ChatInput from '../chat/ChatInput';
import ContentPaste from '../popup/ContentPaste';
import UploadContent from '../popup/UploadContent';
import YouTubeURL from '../popup/YouTubeURL';
import { useAuth } from '../../context/AuthContext';
import { fetchPermittedModels } from '../../utils/fetchSubscription';
import { useNavigate } from 'react-router-dom';
import { db, model } from '../../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAIResponseForSession } from '../../utils/firebaseService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function App() {
  const [darkMode] = useState(() => localStorage.getItem('theme') === 'dark');
  const [spaces] = useState(() => JSON.parse(localStorage.getItem('spaces') || '[]'));
  const { user } = useAuth();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [permittedModels, setPermittedModels] = useState([]);

  const [showContentPastePopup, setShowContentPastePopup] = useState(false);
  const [showYouTubePopup, setShowYouTubePopup] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);

  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  const [activeOverlayTarget, setActiveOverlayTarget] = useState(null); // 'upload' | 'url' | 'youtube' | null

  // Prevent double-submission on chat
  const [isChatSubmitting, setIsChatSubmitting] = useState(false);

  const startGlobalLoading = (message = 'Uploading document...') => {
    setIsGlobalLoading(true);
    toast.dismiss('upload-loader');
    toast.loading(message, {
      toastId: 'upload-loader',
      position: 'top-right',
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      theme: darkMode ? 'dark' : 'light',
    });
  };

  const stopGlobalLoading = ({ success = true, message = '' } = {}) => {
    setIsGlobalLoading(false);
    setActiveOverlayTarget(null); // clear scoped overlay target
    toast.update('upload-loader', {
      render: message || (success ? 'Upload complete' : 'Upload failed'),
      type: success ? 'success' : 'error',
      isLoading: false,
      autoClose: success ? 2000 : 3500,
    });
  }

  // Fetch permitted models for user
  useEffect(() => {
    let active = true;
    (async () => {
      if (!user?.uid) { setPermittedModels([]); return; }
      try {
        const models = await fetchPermittedModels(user.uid);
        if (active) setPermittedModels(models || []);
      } catch (e) {
        if (active) setPermittedModels([]);
      }
    })();
    return () => { active = false; };
  }, [user?.uid]);

  // New handlers for content submission with navigation
  const handleContentSubmit = (data) => {
    // Check if we have a document ID for navigation
    if (data && (data.contentId || data.document_id)) {
      const id = data.contentId || data.document_id;
      return { contentId: id }; // Return object with contentId for the popup components
    }
    return null;
  };

  const handleYouTubeSubmit = (data) => {
    // For YouTube, we can use either contentId, document_id, or videoId
    if (data) {
      const id = data.contentId || data.document_id || data.videoId;
      if (id) {
        return { contentId: id };
      }
    }
    return null;
  };

  const handleFileUpload = (data) => {
    if (data && (data.contentId || data.document_id)) {
      const id = data.contentId || data.document_id;
      return { contentId: id };
    }
    return null;
  };

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('spaces', JSON.stringify(spaces));
  }, [spaces]);

  useEffect(() => {
    document.body.style.overflow = showUploadPopup || showYouTubePopup || showContentPastePopup ? 'hidden' : '';
    return () => (document.body.style.overflow = '');
  }, [showUploadPopup, showYouTubePopup, showContentPastePopup]);

  return (
    <div className="flex flex-col h-screen overflow-hidden lg:mt-16">
      {/* Toasts */}
      <ToastContainer />
      <main className={`flex-1 overflow-y-auto bg-white transition-all duration-300 
        pt-16 md:pt-0 ${document.body.classList.contains('sidebar-collapsed') ? 'md:ml-16' : 'md:ml-64'}`}>
        <div className="w-full max-w-5xl mx-auto p-4 md:p-8 pb-20 mb-10 mt-20">
          <h1 className="text-4xl text-center text-gray-800 dark:text-white mb-8 tracking-tight">
            How do you want to learn today?
          </h1>

          {/* Disable interactions on the card grid while loading */}
          <div
            className={`flex flex-wrap justify-center -m-2 md:-m-3 mb-2 md:mb-12 ${isGlobalLoading ? 'pointer-events-none' : ''}`}
            aria-busy={isGlobalLoading}
          >
            {[
              { key: 'upload', label: 'Upload', emoji: '📄', desc: 'Upload Lecture PDFs, eBooks etc.', tag: 'Popular', onClick: () => setShowUploadPopup(true) },
              { key: 'url', label: 'URL', emoji: '🔗', desc: 'Webpage, Research Papers, PDFs.', onClick: () => setShowContentPastePopup(true) },
              { key: 'youtube', label: 'YouTube', emoji: '✨', desc: 'Give URL of video tutorial or lecture.', onClick: () => setShowYouTubePopup(true) },
            ].map((item, index) => (
              <div key={index} className="w-full md:w-1/2 xl:w-1/3 p-3">
                <div
                  className={`flex flex-col justify-center p-6 bg-white rounded-4xl cursor-pointer hover:shadow-lg transition-all relative`}
                  style={{ background: 'linear-gradient(131deg, #EFECE9 26.82%, #E5F1FB 96.89%)' }}
                  onClick={() => { if (isGlobalLoading) return; setActiveOverlayTarget(item.key); item.onClick(); }}
                  aria-disabled={isGlobalLoading}
                  aria-busy={isGlobalLoading && activeOverlayTarget === item.key}
                >
                  {item.tag && (
                    <span className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full">
                      {item.tag}
                    </span>
                  )}
                  {/* Scoped overlay on the clicked card only; keep overlay interactive */}
                  {isGlobalLoading && activeOverlayTarget === item.key && (
                    <div className="absolute inset-0 z-10 bg-white bg-opacity-70 rounded-4xl flex items-center justify-center pointer-events-auto">
                      <span className="inline-block h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}

                  <div className="flex items-center mb-5">
                    <div className="w-12 h-12 bg-neutral-900 rounded-full flex items-center justify-center mr-4">
                      <span className="text-2xl">{item.emoji}</span>
                    </div>
                    <h4 className="text-2xl font-medium">{item.label}</h4>
                  </div>
                  <p className="text-neutral-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="relative">
            <ChatInput
              chatInput={searchQuery}
              setChatInput={setSearchQuery}
              handleChatSubmit={async (e) => {
                e.preventDefault();
                if (isChatSubmitting) return; // guard: ignore double click/press
                const firstMessage = searchQuery.trim();
                if (!firstMessage) return;

                setIsChatSubmitting(true);
                try {
                  if (!user?.uid) return;
                  const newId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2,8)}`;
                  let title = '';
                  try {
                    const prompt = `Generate a concise, descriptive title (max 7 words, no quotes, capitalize major words) for this chat based on the user's first message. If the message is a question, turn it into a short topic-style title. User message: \n\n${firstMessage}\n\nTitle:`;
                    const result = await model.generateContent(prompt);
                    let raw = '';
                    try {
                      const maybe = result?.response?.text;
                      raw = typeof maybe === 'function' ? await result.response.text() : (typeof maybe === 'string' ? maybe : (typeof result?.text === 'function' ? await result.text() : ''));
                    } catch { raw = ''; }
                    title = (raw || '').split('\n')[0].trim();
                    if (!title) title = firstMessage;
                    if (title.length > 70) title = title.slice(0,67) + '...';
                  } catch { title = firstMessage.length > 60 ? firstMessage.slice(0,57) + '...' : firstMessage; }

                  // Create session doc
                  await setDoc(doc(db, `users/${user.uid}/Sessions/${newId}`), {
                    title,
                    created_via: 'adhoc_chat',
                    first_message: firstMessage,
                    timestamp: serverTimestamp(),
                    updated_at: serverTimestamp()
                  });

                  // Trigger AI pipeline to handle saving user+assistant messages
                  getAIResponseForSession(newId, firstMessage).catch(() => {});

                  // Redirect to the chat page
                  navigate(`/c/${newId}`);
                } catch (_) {
                  // silent
                } finally {
                  setSearchQuery('');
                  setIsChatSubmitting(false);
                }
              }}
              isLoading={isChatSubmitting} // disable input/button during submit
              placeholder="Ask a question or search for content..."
              uid={user?.uid}
              permittedModels={permittedModels}
            />
          </div>
        </div>
      </main>
      
      <ContentPaste 
        isOpen={showContentPastePopup} 
        onClose={() => { 
          setShowContentPastePopup(false); 
          // keep activeOverlayTarget so overlay can appear after closing
        }} 
        onSubmit={handleContentSubmit}
        onStartGlobalLoading={startGlobalLoading}
        onStopGlobalLoading={stopGlobalLoading}
      />
      <YouTubeURL 
        isOpen={showYouTubePopup} 
        onClose={() => { 
          setShowYouTubePopup(false); 
          // keep activeOverlayTarget so overlay can appear after closing
        }} 
        onSubmit={handleYouTubeSubmit}
        onStartGlobalLoading={startGlobalLoading}
        onStopGlobalLoading={stopGlobalLoading}
      />
      <UploadContent 
        isOpen={showUploadPopup} 
        onClose={() => { 
          setShowUploadPopup(false); 
          // keep activeOverlayTarget so overlay can appear after closing
        }} 
        onSubmit={handleFileUpload}
        onStartGlobalLoading={startGlobalLoading}
        onStopGlobalLoading={stopGlobalLoading}
      />
      {/* removed page-level overlay; overlay is rendered inside the clicked card */}
    </div>
  );
}